import { useRoomStore } from '../store/room-store'
import { useUIStore } from '../store/ui-store'

// The document title without any unread prefix. Captured once so the badge can be
// cleared back to it (index.html ships it as "ChatPulse").
const BASE_TITLE = document.title || 'ChatPulse'
const FAVICON_HREF = '/favicon.svg'

interface UnreadState { count: number; quiet: boolean }

/**
 * Unread state across joined, non-muted rooms: the total notification count, plus
 * whether any room has quiet (non-notifying) unread messages. Mirrors the sidebar.
 */
function unreadState(): UnreadState {
  const { rooms } = useRoomStore.getState()
  const muted = useUIStore.getState().mutedRooms
  let count = 0
  let quiet = false
  for (const room of rooms.values()) {
    if (room.membership !== 'join') continue
    if (muted.has(room.roomId)) continue
    count += room.unreadCount
    if (room.unreadCount === 0 && room.hasUnread) quiet = true
  }
  return { count, quiet }
}

function renderTitle({ count, quiet }: UnreadState) {
  document.title = count > 0
    ? `(${count > 99 ? '99+' : count}) ${BASE_TITLE}`
    : quiet
    ? `• ${BASE_TITLE}`
    : BASE_TITLE
}

// --- Favicon badge ------------------------------------------------------------
// The favicon is redrawn on a canvas: the base icon plus a small badge (a count
// for notifying unreads, a plain dot for quiet ones). The base SVG is loaded once.

function faviconLink(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  return link
}

function accentColor(): string {
  const triplet = getComputedStyle(document.documentElement).getPropertyValue('--np-accent').trim()
  return triplet ? `rgb(${triplet})` : '#0891b2'
}

let baseIcon: HTMLImageElement | null = null
let baseReady = false
let pendingDraw: (() => void) | null = null

function ensureBaseLoaded() {
  if (baseIcon) return
  baseIcon = new Image()
  const done = () => { baseReady = true; const d = pendingDraw; pendingDraw = null; d?.() }
  baseIcon.onload = done
  baseIcon.onerror = done // fall back to drawing just the badge on a blank canvas
  baseIcon.src = FAVICON_HREF
}

function renderFavicon(state: UnreadState) {
  const link = faviconLink()
  if (state.count === 0 && !state.quiet) {
    link.type = 'image/svg+xml'
    link.href = FAVICON_HREF
    return
  }

  const draw = () => {
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    if (baseReady && baseIcon && baseIcon.naturalWidth) ctx.drawImage(baseIcon, 0, 0, size, size)

    const r = size * 0.32
    const cx = size - r
    const cy = r
    // Ring punched out of the base so the badge reads clearly at small sizes.
    ctx.beginPath()
    ctx.arc(cx, cy, r + size * 0.06, 0, Math.PI * 2)
    ctx.fillStyle = '#0b1220'
    ctx.fill()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.fillStyle = state.count > 0 ? '#dc2626' : accentColor()
    ctx.fill()

    if (state.count > 0) {
      const label = state.count > 9 ? '9+' : String(state.count)
      ctx.fillStyle = '#fff'
      ctx.font = `bold ${label.length > 1 ? size * 0.34 : size * 0.44}px system-ui, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(label, cx, cy + size * 0.02)
    }

    link.type = 'image/png'
    link.href = canvas.toDataURL('image/png')
  }

  if (baseReady) draw()
  else { pendingDraw = draw; ensureBaseLoaded() }
}

let started = false

/**
 * Keep the browser tab title and favicon in sync with the unread count so there's
 * a persistent, glanceable indication of new messages even when the app isn't the
 * focused tab. The Matrix sync keeps running in the background, so the store stays
 * fresh; we just reflect it into the title + favicon. Idempotent.
 */
export function setupTabBadge(): void {
  if (started) return
  started = true

  let last = ''
  const update = () => {
    const state = unreadState()
    const key = `${state.count}|${state.quiet}`
    if (key === last) return
    last = key
    renderTitle(state)
    renderFavicon(state)
  }

  update()
  // unread counts live in the room store; muting lives in the ui store.
  useRoomStore.subscribe(update)
  useUIStore.subscribe(update)
}
